import { ChevronDown, ChevronLeft, Pin, PinOff, X } from 'lucide-react'
import type { ClassValue } from 'clsx'
import type { PropsWithChildren } from 'react'
import { cn } from '@/shared/lib/utils'
import { buttonVariants } from '@/shared/ui/button'
import { appTitle } from '@/shared/config/app-config'

interface Props extends PropsWithChildren {
  variant: 'overlay' | 'pinned'
  isOpen?: boolean
  onClose?: () => void
  onTogglePin?: () => void
  isMainNavigationExpanded?: boolean
  onToggleMainNavigation?: () => void
  isDesktop?: boolean
  classNames?: Array<ClassValue>
}

export function SidePanel({
  variant,
  isOpen = false,
  onClose,
  onTogglePin,
  isMainNavigationExpanded = true,
  onToggleMainNavigation,
  isDesktop = false,
  classNames = [],
  children,
}: Props) {
  if (variant === 'pinned') {
    return (
      <PinnedSidePanel
        classNames={classNames}
        onTogglePin={onTogglePin}
        isMainNavigationExpanded={isMainNavigationExpanded}
        onToggleMainNavigation={onToggleMainNavigation}
      >
        {children}
      </PinnedSidePanel>
    )
  }

  return (
    <OverlaySidePanel
      classNames={classNames}
      isOpen={isOpen}
      onClose={onClose}
      onTogglePin={onTogglePin}
      isDesktop={isDesktop}
    >
      {children}
    </OverlaySidePanel>
  )
}

function PinnedSidePanel({
  classNames = [],
  onTogglePin,
  isMainNavigationExpanded,
  onToggleMainNavigation,
  children,
}: Omit<Props, 'variant' | 'isOpen' | 'onClose' | 'isDesktop'>) {
  return (
    <aside
      className={cn(
        'flex h-full w-80 shrink-0 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-lg',
        ...classNames,
      )}
    >
      <div className="flex items-center justify-between border-b border-sidebar-border p-4">
        <h2 className="text-xl font-bold">{appTitle} Navigation</h2>
        <PinnedNavigationToggle
          isMainNavigationExpanded={isMainNavigationExpanded}
          onToggleMainNavigation={onToggleMainNavigation}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      <div className="flex justify-end border-t border-sidebar-border p-4">
        <button
          onClick={onTogglePin}
          className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
          aria-label="Unpin navigation"
        >
          <PinOff />
        </button>
      </div>
    </aside>
  )
}

function PinnedNavigationToggle({
  isMainNavigationExpanded = true,
  onToggleMainNavigation,
}: Pick<Props, 'isMainNavigationExpanded' | 'onToggleMainNavigation'>) {
  if (!onToggleMainNavigation) {
    return null
  }

  return (
    <button
      type="button"
      onClick={onToggleMainNavigation}
      className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
      data-state={isMainNavigationExpanded ? 'expanded' : 'collapsed'}
      aria-label={
        isMainNavigationExpanded
          ? 'Collapse navigation menu'
          : 'Expand navigation menu'
      }
    >
      <span
        aria-hidden="true"
        className="relative block size-4 motion-reduce:transition-none"
      >
        <span
          className={cn(
            'absolute inset-0 transition-all duration-200 ease-in-out motion-reduce:transition-none',
            isMainNavigationExpanded
              ? 'rotate-0 opacity-100'
              : '-rotate-90 opacity-0',
          )}
        >
          <ChevronDown className="size-4" />
        </span>
        <span
          className={cn(
            'absolute inset-0 transition-all duration-200 ease-in-out motion-reduce:transition-none',
            isMainNavigationExpanded
              ? 'rotate-90 opacity-0'
              : 'rotate-0 opacity-100',
          )}
        >
          <ChevronLeft className="size-4" />
        </span>
      </span>
    </button>
  )
}

function OverlaySidePanel({
  classNames = [],
  isOpen = false,
  onClose,
  onTogglePin,
  isDesktop = false,
  children,
}: Pick<
  Props,
  'classNames' | 'isOpen' | 'onClose' | 'onTogglePin' | 'isDesktop' | 'children'
>) {
  return (
    <aside
      className={cn(
        `fixed top-0 left-0 z-50 flex h-full w-80 transform flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`,
        ...classNames,
      )}
    >
      <div className="flex items-center justify-between border-b border-sidebar-border p-4">
        <h2 className="text-xl font-bold">{appTitle} navigation</h2>
        <button
          onClick={onClose}
          className="cursor-pointer rounded-lg p-2 text-sidebar-foreground/80 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          aria-label="Close menu"
        >
          <X size={24} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      {isDesktop && onTogglePin ? (
        <div className="flex justify-end border-t border-sidebar-border p-4">
          <button
            onClick={onTogglePin}
            className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
            aria-label="Pin navigation"
          >
            <Pin />
          </button>
        </div>
      ) : null}
    </aside>
  )
}
